// ---------- le panneau du compte ----------
// Ouvert par le bouton de profil de l'accueil (src/home.ts). Deux choses et pas plus : lire son
// code de reprise (et le copier), et en taper un pour reprendre ailleurs une progression. Le
// compte lui-même n'a ni nom ni réglage : il n'y a rien d'autre à y montrer.
//
// Construit à la demande et posé sur <body>, hors des trois écrans : il passe par-dessus le chemin
// sans le reconstruire, et l'accueil n'a qu'un `data-act` de plus à écouter.

import './css/account.css';
import {
  adoptUser,
  currentUser,
  ensureUser,
  fetchRecoveryCode,
  normalizeCode,
  redeemRecoveryCode,
} from './account';
import { t } from './i18n';
import { resyncAll } from './sync';
import { esc, ICON } from './ui';

let box: HTMLElement | null = null;
/** Le code affiché, pour reconnaître « c'est déjà le mien » sans aller-retour. */
let mine: string | null = null;
/** Ce qui redessine l'accueil après une reprise, donné par l'appelant. */
let refresh: () => void = () => {};

const q = <T extends HTMLElement>(sel: string): T => box!.querySelector<T>(sel)!;

/**
 * Ouvre le panneau et va chercher le code. Ouvrir ce panneau CRÉE le compte s'il n'existe pas
 * encore, et c'est voulu : demander son code de reprise est la meilleure raison qui soit d'avoir un
 * compte, et le joueur qui le note veut qu'il désigne quelque chose. Le code lui-même n'est posé
 * en base qu'à ce premier appel (`GET /me/recovery`), jamais pour qui n'ouvre pas le panneau.
 */
export async function openAccount(onRestored: () => void): Promise<void> {
  refresh = onRestored;
  if (!box) build();
  box!.hidden = false;
  document.addEventListener('keydown', onKey);
  q<HTMLInputElement>('.acct-input').value = '';
  say('');
  const code = q('.acct-code');
  const copy = q<HTMLButtonElement>('.acct-copy');
  copy.textContent = t('account.copy');
  copy.disabled = true;
  code.textContent = t('account.loading');
  code.classList.add('wait');
  q<HTMLButtonElement>('.acct-close').focus();

  let phrase: string | null = null;
  try {
    const token = await ensureUser();
    if (token) phrase = await fetchRecoveryCode(token);
  } catch {
    /* hors ligne, ou Worker injoignable : on le dit, sans fermer */
  }
  if (box!.hidden) return;
  mine = phrase;
  code.textContent = phrase ?? t('account.offline');
  code.classList.toggle('wait', !phrase);
  copy.disabled = !phrase;
}

/** Le panneau est-il ouvert ? Le bouton Retour d'Android le referme avant toute autre chose. */
export const accountOpen = (): boolean => !!box && !box.hidden;

export function close(): void {
  if (!box) return;
  box.hidden = true;
  document.removeEventListener('keydown', onKey);
}

function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') close();
  if (e.key === 'Enter' && document.activeElement === q('.acct-input')) void restore();
}

function say(text: string, ok = false): void {
  const el = q('.acct-msg');
  el.textContent = text;
  el.classList.toggle('ok', ok);
}

function build(): void {
  box = document.createElement('div');
  box.className = 'acct';
  box.hidden = true;
  box.innerHTML = `
    <div class="acct-card" role="dialog" aria-modal="true" aria-labelledby="acct-title">
      <button class="acct-close" type="button" aria-label="${esc(t('account.close'))}">${ICON.close}</button>
      <h2 id="acct-title">${esc(t('account.title'))}</h2>
      <p>${esc(t('account.intro'))}</p>
      <small class="acct-label">${esc(t('account.codeLabel'))}</small>
      <div class="acct-row">
        <output class="acct-code" aria-live="polite"></output>
        <button class="btn btn-go acct-copy" type="button"></button>
      </div>
      <hr>
      <h3>${esc(t('account.restoreTitle'))}</h3>
      <p>${esc(t('account.restoreText'))}</p>
      <input class="acct-input" type="text" autocomplete="off" autocapitalize="none" autocorrect="off" spellcheck="false" enterkeyhint="go" placeholder="${esc(t('account.placeholder'))}" aria-label="${esc(t('account.restoreTitle'))}">
      <button class="btn btn-yellow acct-use" type="button">${esc(t('account.restore'))}</button>
      <p class="acct-msg" role="status"></p>
    </div>`;
  // Toucher le voile ferme ; toucher la carte, non.
  box.addEventListener('click', (e) => {
    if (e.target === box) close();
  });
  q('.acct-close').addEventListener('click', close);
  q('.acct-copy').addEventListener('click', () => void copy());
  q('.acct-use').addEventListener('click', () => void restore());
  document.body.append(box);
}

/** Le presse-papiers peut être refusé (WebView sans permission, page non sécurisée) : on
 *  sélectionne alors le code, pour qu'un appui long suffise à le copier à la main. */
async function copy(): Promise<void> {
  if (!mine) return;
  const btn = q<HTMLButtonElement>('.acct-copy');
  try {
    await navigator.clipboard.writeText(mine);
    btn.textContent = t('account.copied');
  } catch {
    getSelection()?.selectAllChildren(q('.acct-code'));
  }
}

async function restore(): Promise<void> {
  const input = q<HTMLInputElement>('.acct-input');
  const btn = q<HTMLButtonElement>('.acct-use');
  const typed = normalizeCode(input.value);
  if (!typed) return;
  if (typed === mine) return say(t('account.same'));
  btn.disabled = true;
  btn.textContent = t('account.restoring');
  say('');
  try {
    const found = await redeemRecoveryCode(typed);
    if (found === null) return say(t('account.unknown'));
    if (found === 'busy') return say(t('account.busy'));
    if (found !== currentUser()) {
      adoptUser(found);
      await resyncAll();
    }
    mine = typed;
    q('.acct-code').textContent = typed;
    q<HTMLButtonElement>('.acct-copy').disabled = false;
    input.value = '';
    say(t('account.done'), true);
    refresh();
  } catch {
    say(t('account.failed'));
  } finally {
    btn.disabled = false;
    btn.textContent = t('account.restore');
  }
}
